/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Apr 16 2024
 **/

import { useQuery, useQueryClient } from "@tanstack/react-query";

import { Properties } from "@/frameworks/libraries/properties";
import { useChat } from "@/frameworks/core/API";

const useQueryCache = <T>(queryKey: string[]) => {
	const { data } = useQuery<T>({
		queryKey,
		staleTime: Infinity,
		enabled: true
	});

	return data;
};

/**
 * 채팅에서 전체 스페이스 조회를 위한 훅입니다. ( 그룹핑 버전 )
 */
export const useGroupSpaceListState = () => {
	const groupSpaceGroupListMap = useQueryCache<Map<string, WebServices.SpaceChat.GroupedList.Data>>([
		"getGroupSpaceChatList"
	]);

	const myID = String(Properties.property.user?.myRoomId);
	const chatAPI = useChat();
	const isGuest = Properties.property.isGuest;

	const { data } = useQuery({
		queryKey: ["getGroupsSpaceChatListOfGuest"],
		queryFn: async () => {
			const { data } = await chatAPI.space.groupedList.execute();
			return data;
		},
		enabled: isGuest
	});

	return {
		myRoomChat: groupSpaceGroupListMap?.get(myID)?.myRoomChat,
		groupedSpaceChats: !isGuest ? groupSpaceGroupListMap?.get(myID)?.groupedSpaceChats : data?.groupedSpaceChats
	};
};

/**
 * 채팅에서 전체 스페이스 조회를 위한 훅입니다.
 */
export const useSpaceListState = () => {
	const spaceListMap = useQueryCache<Map<string, WebServices.SpaceChat.List.Data[number]>>(["getSpaceChatList"]);

	if (spaceListMap) {
		return [...spaceListMap.values()];
	}
	return [];
};

/**
 * 채팅에서 단일 스페이스 조회를 위한 훅입니다.
 */
export const useSpaceChannelState = (spaceID: string) => {
	const spaceListMap = useQueryCache<Map<string, WebServices.SpaceChat.List.Data[number]>>(["getSpaceChatList"]);

	const space = spaceListMap?.get(spaceID);

	useQueryCache(["getSpaceChatList"]);
	return space;
};

/**
 *  채팅에서 단일 스페이스의 단일 채팅방 조회를 위한 훅입니다.
 */
export const useSpaceRoomState = ({ spaceID, roomID }: { spaceID: string; roomID: string }) => {
	const spaceListMap = useQueryCache<Map<string, WebServices.SpaceChat.List.Data[number]>>(["getSpaceChatList"]);
	const space = spaceListMap?.get(spaceID);

	return space?.rooms.find((v) => v.roomId === roomID);
};

/**
 * 전체 DM 조회를 위한 훅입니다.
 */
export const useDMRoomListState = () => {
	const dmRoomList = useQueryCache<Map<string, WebServices.DirectMessage.List.Data[number]>>(["getDMList"]);

	useQueryCache(["getDMList"]);
	if (dmRoomList) {
		return [...dmRoomList.values()];
	}
	return [];
};

/**
 * 단일 DM 조회를 위한 훅입니다.
 */
export const useDMRoomState = (roomID: string) => {
	const dmRoom = useQueryCache<Map<string, WebServices.DirectMessage.List.Data[number]>>(["getDMList"])?.get(roomID);
	useQueryCache(["getDMList"]);
	return dmRoom;
};

/**
 * 단일 채팅방 조회를 위한 훅입니다.
 */
export const useRoomState = (roomID: string) => {
	const dmRoom = useQueryCache<Map<string, WebServices.DirectMessage.List.Data[number]>>(["getDMList"])?.get(roomID);
	const spaceChatListMap = useQueryCache<Map<string, WebServices.SpaceChat.List.Data[number]>>(["getSpaceChatList"]);

	if (dmRoom) return dmRoom;

	const spaceChatList = spaceChatListMap ? [...spaceChatListMap.values()] : [];

	const space = spaceChatList.filter((space) => {
		return space.rooms.some((room) => room.roomId === roomID);
	})[0];

	if (space) {
		return space.rooms.find((room) => room.roomId === roomID);
	}
};

/**
 * spaceChatList를 업데이트를 위한 훅입니다.
 */
type SpaceChatListMap = Map<string, WebServices.SpaceChat.List.Data[number]>;
export const useSetSpaceChatRoomList = () => {
	const queryClient = useQueryClient();

	return (updater: (data: SpaceChatListMap | undefined) => SpaceChatListMap) => {
		queryClient.setQueryData<SpaceChatListMap>(["getSpaceChatList"], (data) => {
			return updater(data);
		});
	};
};
